import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  alpha,
  useTheme,
  Skeleton,
  TextField,
} from '@mui/material';
import {
  CheckCircle,
  AccessTime,
  Cancel,
  TrendingUp,
  CalendarMonth,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useSnackbar } from 'notistack';
import CardTooltip from '../../components/common/CardTooltip';
import myAttendanceService, {
  MyAttendanceData,
  MyAttendanceSummaryData,
  MyAttendanceRecord,
} from '../../services/myAttendanceService';
import useTimezone from '../../hooks/useTimezone';

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    return (
      <Paper
        sx={{
          p: 1.5,
          backgroundColor: alpha(theme.palette.background.paper, 0.95),
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography
            key={index}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {entry.name}: {entry.value}
          </Typography>
        ))}
      </Paper>
    );
  }
  return null;
};

const MyAttendancePage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { isFutureDate } = useTimezone();

  // State
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState<MyAttendanceData | null>(null);
  const [summaryData, setSummaryData] = useState<MyAttendanceSummaryData | null>(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Fetch attendance data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [monthData, yearSummary] = await Promise.all([
        myAttendanceService.getMyAttendance(selectedYear, selectedMonth),
        myAttendanceService.getMyAttendanceSummary(selectedYear),
      ]);
      setAttendanceData(monthData);
      setSummaryData(yearSummary);
    } catch (error: any) {
      enqueueSnackbar(error.message || 'Failed to load attendance data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Status color mapping
  const getStatusColor = (status: string | null): string => {
    switch (status) {
      case 'PRESENT': return theme.palette.success.main;
      case 'LATE': return theme.palette.warning.main;
      case 'HALF_DAY': return '#2196f3';
      case 'ABSENT': return theme.palette.error.main;
      case 'LEAVE': return '#06b6d4'; // Cyan for Leave
      case 'HOLIDAY': return '#8ac02dff'; // User preferred Orange for Holiday
      case 'SCHEDULED_OFF': return '#9c27b0'; // Purple for visibility
      default: return '#78909c'; // Blue-grey for Not Marked
    }
  };

  // Render summary stat card with optional tooltip
  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    suffix = '',
    tooltip
  }: { 
    title: string; 
    value: number | string; 
    icon: React.ReactNode; 
    color: string;
    suffix?: string;
    tooltip?: React.ReactNode;
  }) => {
    const cardContent = (
      <Card
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          borderRadius: 3,
          transition: 'all 0.3s ease',
          cursor: tooltip ? 'help' : 'default',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 24px ${alpha(color, 0.2)}`,
          },
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {title}
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ color }}>
                {value}{suffix}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(color, 0.15),
                color,
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );

    if (tooltip) {
      return (
        <CardTooltip title={tooltip}>
          {cardContent}
        </CardTooltip>
      );
    }

    return cardContent;
  };

  // Loading skeleton
  if (loading && !attendanceData) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={120} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Skeleton variant="rounded" height={300} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  const summary = attendanceData?.summary;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            My Attendance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your attendance records and statistics
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small"
            label="Year"
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value) || new Date().getFullYear())}
            sx={{ width: 100 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
            >
              {monthNames.map((name, index) => (
                <MenuItem key={index} value={index + 1}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

           {/* Yearly Charts Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          📊 {selectedYear} Year Overview
        </Typography>
      </Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Pie Chart */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              minHeight: 350,
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Attendance Breakdown ({selectedYear})
            </Typography>
            {summaryData?.breakdown && summaryData.breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={summaryData.breakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {summaryData.breakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value) => <span style={{ color: theme.palette.text.primary }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Alert severity="info" sx={{ maxWidth: 300 }}>
                  No attendance data recorded for {selectedYear}
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Area Chart - Monthly Trend */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              height: '100%',
              minHeight: 350,
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Monthly Attendance Trend ({selectedYear})
            </Typography>
            {summaryData?.monthlyTrends && summaryData.monthlyTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={summaryData.monthlyTrends}>
                  <defs>
                    <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorLate" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.warning.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.warning.main} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorAbsent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={theme.palette.error.main} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={theme.palette.error.main} stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorLeave" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="monthName" 
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    tickFormatter={(value) => value.substring(0, 3)}
                  />
                  <YAxis tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="present"
                    name="Present"
                    stroke={theme.palette.success.main}
                    fillOpacity={1}
                    fill="url(#colorPresent)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="late"
                    name="Late"
                    stroke={theme.palette.warning.main}
                    fillOpacity={1}
                    fill="url(#colorLate)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="leaves"
                    name="On Leave"
                    stroke="#06b6d4"
                    fillOpacity={1}
                    fill="url(#colorLeave)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="absent"
                    name="Absent"
                    stroke={theme.palette.error.main}
                    fillOpacity={1}
                    fill="url(#colorAbsent)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Alert severity="info" sx={{ maxWidth: 300 }}>
                  No monthly trend data available
                </Alert>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly Summary Cards */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          📅 {monthNames[selectedMonth - 1]} {selectedYear} Summary
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            title="Attendance Rate"
            value={summary?.attendancePercentage ?? 0}
            suffix="%"
            icon={<TrendingUp />}
            color={theme.palette.primary.main}
            tooltip={
              <Box sx={{ fontSize: '0.85rem' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  📊 Attendance Rate Formula
                </Typography>
                <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontFamily: 'monospace' }}>
                  (Present + Late + Half Day) / (Working Days - Leaves) × 100
                </Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Working Days:</strong> Excludes Scheduled Off & Not Scheduled
                </Typography>
                {summary && summary.totalDays > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    = ({summary.present} + {summary.late} + {summary.halfDay}) / ({summary.totalDays} - {summary.leaves}) × 100
                    <br />
                    = <strong>{summary.attendancePercentage}%</strong>
                  </Typography>
                )}
              </Box>
            }
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            title="Present Days"
            value={summary?.present ?? 0}
            icon={<CheckCircle />}
            color={theme.palette.success.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            title="Leaves"
            value={summary?.leaves ?? 0}
            icon={<CalendarMonth />}
            color="#06b6d4"
          />
        </Grid>
        {/* <Grid size={{ xs: 6, sm: 6, md: 3 }}>
          <StatCard
            title="Late / Half Day"
            value={`${summary?.late ?? 0} / ${summary?.halfDay ?? 0}`}
            icon={<AccessTime />}
            color={theme.palette.warning.main}
          />
          <StatCard
            title="Leaves"
            value={summary?.leaves ?? 0}
            icon={<CalendarMonth />}
            color="#06b6d4"
          />
        </Grid> */}
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            title="Late / Half Day"
            value={`${summary?.late ?? 0} / ${summary?.halfDay ?? 0}`}
            icon={<AccessTime />}
            color={theme.palette.warning.main}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 2.4 }}>
          <StatCard
            title="Absent Days"
            value={summary?.absent ?? 0}
            icon={<Cancel />}
            color={theme.palette.error.main}
          />
        </Grid>
      </Grid>

      {/* Detailed Records Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonth color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {attendanceData?.monthName} {selectedYear} - Daily Records
            </Typography>
          </Box>
        </Box>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Day</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>First Punch</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Last Punch</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Work Duration</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Late By</TableCell>
                <TableCell sx={{ fontWeight: 600, backgroundColor: theme.palette.background.default }}>Remarks</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendanceData?.records.map((record: MyAttendanceRecord) => {
                const isFuture = isFutureDate(record.date);
                
                return (
                  <TableRow
                    key={record.date}
                    sx={{
                      opacity: isFuture ? 0.5 : 1,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {new Date(record.date).toLocaleDateString('en-US', { 
                          day: '2-digit', 
                          month: 'short' 
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.dayName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {record.status ? (
                        <Chip
                          label={record.status.charAt(0) + record.status.slice(1).toLowerCase().replace('_', ' ')}
                          size="small"
                          sx={{
                            backgroundColor: alpha(getStatusColor(record.status), 0.15),
                            color: getStatusColor(record.status),
                            fontWeight: 500,
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled">
                          {isFuture ? 'Upcoming' : 'Not Marked'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.firstPunch 
                          ? new Date(record.firstPunch).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.lastPunch 
                          ? new Date(record.lastPunch).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {record.totalWorkMinutes 
                          ? `${Math.floor(record.totalWorkMinutes / 60)}h ${record.totalWorkMinutes % 60}m`
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={record.lateByMinutes && record.lateByMinutes > 0 ? 'warning.main' : 'text.secondary'}
                        fontWeight={record.lateByMinutes && record.lateByMinutes > 0 ? 500 : 400}
                      >
                        {record.lateByMinutes && record.lateByMinutes > 0 
                          ? `${record.lateByMinutes} min`
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {record.leaveType ? (
                          <Box component="span" sx={{ color: '#0097a7', fontWeight: 500 }}>
                            {record.leaveType}
                          </Box>
                        ) : (record.remarks || '-')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default MyAttendancePage;
